import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Building2, Wrench, Send, FileText, Eye, EyeOff, Plus, MapPin, ChevronDown } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';
import { toast } from 'sonner';
import { SearchableSelect } from '../components/search/SearchableSelect';
import { normalizzaPartitaIva, partitaIvaValida } from '../utils/partitaIva';
import { IUBENDA_PRIVACY_URL } from '../components/legal/iubenda';
import { ProvinciaSelect } from '../components/form/ProvinciaSelect';

const API = process.env.REACT_APP_BACKEND_URL;

interface MarcaLookup {
  code: string;
  label: string;
}
interface MarcheResponse {
  marche: MarcaLookup[];
}

// ── Validazione ────────────────────────────────────────────────────────────
// Un campo "obbligatorio" è vuoto anche se contiene solo spazi: i valori
// vengono trimmati solo al submit, quindi il controllo lo fa la refine.
const requiredText = (message = 'Campo obbligatorio') =>
  z.string().refine(v => v.trim().length > 0, message);

// Partita IVA italiana: 11 cifre con cifra di controllo, prefisso "IT" e
// separatori ammessi (in invio la mandiamo normalizzata, come la salva il backend).

// Telefono/cellulare: almeno 8 cifre, con eventuale prefisso internazionale.
const PHONE_RE = /^\+?[\d\s.\-()]{8,}$/;
const countDigits = (v: string) => (v.match(/\d/g) || []).length;

const PEC_SDI_MESSAGE = 'Compila almeno uno tra PEC e Codice SDI';

const registerSchema = z
  .object({
    // Credenziali
    email: z.email('Inserisci un indirizzo email valido'),
    password: z.string().min(8, 'La password deve avere almeno 8 caratteri'),
    passwordConfirm: z.string().min(1, 'Conferma la password'),
    // Dati azienda
    ragioneSociale: requiredText(),
    partitaIva: z
      .string()
      .refine(v => v.trim().length > 0, 'Campo obbligatorio')
      .refine(
        v => !v.trim() || /^\d{11}$/.test(normalizzaPartitaIva(v)),
        'La partita IVA deve essere di 11 cifre (es. 01234567897 o IT01234567897)',
      )
      .refine(
        v => !/^\d{11}$/.test(normalizzaPartitaIva(v)) || partitaIvaValida(normalizzaPartitaIva(v)),
        'Partita IVA non valida: controlla le cifre',
      ),
    codiceFiscale: z.string(),
    pec: z.string().refine(v => !v.trim() || z.email().safeParse(v.trim()).success, 'Indirizzo PEC non valido'),
    codiceSdi: z.string(),
    telefonoAzienda: z.string().refine(v => !v.trim() || PHONE_RE.test(v.trim()), 'Numero di telefono non valido'),
    // Dati referente
    nome: requiredText(),
    cognome: requiredText(),
    cellulare: z
      .string()
      .refine(v => v.trim().length > 0, 'Campo obbligatorio')
      .refine(v => !v.trim() || (PHONE_RE.test(v.trim()) && countDigits(v) >= 8), 'Numero di cellulare non valido'),
    // Sede legale
    indirizzo: requiredText(),
    cap: z.string().regex(/^\d{5}$/, 'Il CAP deve essere di 5 cifre'),
    citta: requiredText(),
    provincia: z.string().regex(/^[A-Za-z]{2}$/, 'Sigla provincia di 2 lettere (es. MI)'),
    // Sede operativa (opzionale)
    hasSedeOperativa: z.boolean(),
    sedeOpIndirizzo: z.string(),
    sedeOpCap: z.string(),
    sedeOpCitta: z.string(),
    sedeOpProvincia: z.string(),
    // Profilazione
    marcheInteresse: z.array(z.string()).min(1, 'Seleziona almeno una marca di interesse'),
    parcoMacchineStimato: z.string(),
    // Consenso
    accettaPrivacy: z.boolean().refine(v => v === true, 'Devi accettare la privacy policy per proseguire'),
  })
  .superRefine((data, ctx) => {
    if (data.passwordConfirm && data.password !== data.passwordConfirm) {
      ctx.addIssue({ code: 'custom', path: ['passwordConfirm'], message: 'Le password non coincidono' });
    }
    // Fatturazione elettronica: almeno uno tra PEC e Codice SDI. L'errore va
    // segnalato su entrambi i campi (bordo rosso: l'uno o l'altro risolve), ma
    // il testo è mostrato una volta sola dalla riga di avviso sotto i due —
    // vedi PEC_SDI_MESSAGE / fieldOnlyError.
    if (!data.pec.trim() && !data.codiceSdi.trim()) {
      ctx.addIssue({ code: 'custom', path: ['pec'], message: PEC_SDI_MESSAGE });
      ctx.addIssue({ code: 'custom', path: ['codiceSdi'], message: PEC_SDI_MESSAGE });
    }
    if (data.hasSedeOperativa) {
      const conditional: [keyof typeof data, string][] = [
        ['sedeOpIndirizzo', data.sedeOpIndirizzo],
        ['sedeOpCitta', data.sedeOpCitta],
      ];
      conditional.forEach(([path, value]) => {
        if (!value.trim()) ctx.addIssue({ code: 'custom', path: [path], message: 'Campo obbligatorio' });
      });
      if (!/^\d{5}$/.test(data.sedeOpCap)) {
        ctx.addIssue({ code: 'custom', path: ['sedeOpCap'], message: 'Il CAP deve essere di 5 cifre' });
      }
      if (!/^[A-Za-z]{2}$/.test(data.sedeOpProvincia)) {
        ctx.addIssue({ code: 'custom', path: ['sedeOpProvincia'], message: 'Sigla provincia di 2 lettere (es. MI)' });
      }
    }
  });

type RegisterForm = z.infer<typeof registerSchema>;

const defaultValues: RegisterForm = {
  email: '', password: '', passwordConfirm: '',
  ragioneSociale: '', partitaIva: '', codiceFiscale: '', pec: '', codiceSdi: '', telefonoAzienda: '',
  nome: '', cognome: '', cellulare: '',
  indirizzo: '', cap: '', citta: '', provincia: '',
  hasSedeOperativa: false,
  sedeOpIndirizzo: '', sedeOpCap: '', sedeOpCitta: '', sedeOpProvincia: '',
  marcheInteresse: [], parcoMacchineStimato: '',
  accettaPrivacy: false,
};

const PARCO_OPTIONS = [
  { value: '<50', label: 'Meno di 50' },
  { value: '50-200', label: 'Da 50 a 200' },
  { value: '200-500', label: 'Da 200 a 500' },
  { value: '500+', label: 'Oltre 500' },
];

// Componenti definiti a livello di modulo (non dentro RegisterPage) — se fossero
// dichiarati dentro il render del componente, React li tratterebbe come un
// nuovo "tipo" di componente ad ogni render (ogni keystroke), smontando e
// rimontando l'<input> DOM sottostante e facendo perdere il focus.
const Label = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <label className="text-xs font-semibold text-foreground mb-1 block">
    {children}{required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

// `ComponentPropsWithRef` e non `InputHTMLAttributes`: react-hook-form passa una
// `ref` tra le props di `register(...)` e va inoltrata all'<input> reale.
const Input = ({ error, className, ...props }: React.ComponentPropsWithRef<'input'> & { error?: boolean }) => (
  <input
    {...props}
    aria-invalid={error || undefined}
    className={`w-full h-10 px-3 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:border-transparent ${
      error
        ? 'border-red-400 focus:ring-red-400'
        : 'focus:ring-[hsl(214,58%,41%)]'
    } ${className || ''}`}
  />
);

const FieldError = ({ children }: { children?: React.ReactNode }) =>
  children ? <p className="text-[11px] text-red-500 mt-1">{children}</p> : null;

// PEC e Codice SDI condividono un unico vincolo ("almeno uno dei due"): il
// messaggio lo mostra la riga di avviso che sta sotto entrambi, quindi sotto
// al singolo input restano solo gli errori specifici di quel campo.
const fieldOnlyError = (message?: string) => (message === PEC_SDI_MESSAGE ? undefined : message);

export default function RegisterPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showMarcaModal, setShowMarcaModal] = useState(false);
  const [marcaSearch, setMarcaSearch] = useState('');
  const marcaRef = useRef<HTMLDivElement>(null);
  const [brands, setBrands] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    trigger,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const hasSedeOperativa = watch('hasSedeOperativa');
  const marcheInteresse = watch('marcheInteresse');
  const pec = watch('pec');
  const codiceSdi = watch('codiceSdi');
  const pecOrSdiOk = Boolean(pec?.trim() || codiceSdi?.trim());
  const pecSdiError = errors.pec?.message === PEC_SDI_MESSAGE || errors.codiceSdi?.message === PEC_SDI_MESSAGE;

  // PEC e Codice SDI condividono un solo vincolo, ma react-hook-form rivalida
  // soltanto il campo che l'utente ha toccato: compilando la PEC, l'errore
  // resterebbe acceso sul Codice SDI (bordo rosso) anche a vincolo ormai
  // soddisfatto. Il re-trigger sui due campi li riallinea.
  useEffect(() => {
    if (pecSdiError && pecOrSdiOk) trigger(['pec', 'codiceSdi']);
  }, [pecSdiError, pecOrSdiOk, trigger]);

  // Carica marche dalla lookup NTS (collection erp_lookups → Tabmarc)
  useEffect(() => {
    axios
      .get<MarcheResponse>(`${API}/api/lookups/marche`)
      .then(res => setBrands((res.data.marche || []).map(m => m.label)))
      .catch(() => setBrands([]));
  }, []);

  // Chiudi modale marca al click esterno
  useEffect(() => {
    if (!showMarcaModal) return;
    const handler = (e: MouseEvent) => {
      if (marcaRef.current && !marcaRef.current.contains(e.target as Node)) {
        setShowMarcaModal(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMarcaModal]);

  const setMarche = (next: string[]) =>
    setValue('marcheInteresse', next, { shouldValidate: true, shouldDirty: true });

  const removeMarca = (v: string) => setMarche(marcheInteresse.filter(m => m !== v));

  const onSubmit = async (values: RegisterForm) => {
    try {
      const body: Record<string, unknown> = {
        email: values.email.trim(),
        password: values.password,
        nome: values.nome.trim(),
        cognome: values.cognome.trim(),
        cellulare: values.cellulare.trim(),
        telefono: values.telefonoAzienda.trim() || undefined,
        ragioneSociale: values.ragioneSociale.trim(),
        partitaIva: normalizzaPartitaIva(values.partitaIva),
        codiceFiscale: values.codiceFiscale.trim() || undefined,
        pec: values.pec.trim() || undefined,
        codiceSdi: values.codiceSdi.trim() || undefined,
        indirizzo: values.indirizzo.trim(),
        cap: values.cap.trim(),
        citta: values.citta.trim(),
        provincia: values.provincia.trim().toUpperCase(),
        marcheInteresse: values.marcheInteresse,
        parcoMacchineStimato: values.parcoMacchineStimato || undefined,
        sedeOperativa: values.hasSedeOperativa ? {
          indirizzo: values.sedeOpIndirizzo.trim(),
          citta: values.sedeOpCitta.trim(),
          cap: values.sedeOpCap.trim(),
          provincia: values.sedeOpProvincia.trim().toUpperCase(),
        } : undefined,
      };
      await axios.post(`${API}/api/auth/register`, body);
      toast.success('Registrazione inviata! Riceverai le credenziali via email dopo la verifica.');
      navigate('/accedi');
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Errore durante la registrazione';
      // Partita IVA già di un altro account: il messaggio resta sul campo,
      // perché dice cosa fare (farsi aggiungere come Operatore dal Titolare).
      if (err?.response?.status === 409 && typeof msg === 'string' && msg.includes('Partita IVA')) {
        setError('partitaIva', { message: msg }, { shouldFocus: true });
      }
      toast.error(typeof msg === 'string' ? msg : 'Errore durante la registrazione');
    }
  };

  const onInvalid = () => toast.error('Controlla i campi evidenziati in rosso');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50/40 via-white to-blue-50/40" data-testid="register-page">
      <header className="border-b bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/accedi" className="flex items-center gap-2 hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Torna al login
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-2">Richiedi accesso B2B</h1>
          <p className="text-muted-foreground text-sm">
            Compila il modulo. Il nostro team verifica la richiesta e ti invia le credenziali via email.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6" noValidate data-testid="register-form">
          {/* === Account === */}
          <section className="rounded-2xl border bg-white shadow-sm p-6" data-testid="section-account">
            <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[hsl(214,58%,41%)]" /> Credenziali di accesso
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label required>Email</Label>
                <Input type="email" placeholder="email@azienda.it" data-testid="reg-email" error={!!errors.email} {...register('email')} />
                <FieldError>{errors.email?.message}</FieldError>
              </div>
              <div className="relative">
                <Label required>Password</Label>
                <Input type={showPassword ? 'text' : 'password'} placeholder="min 8 caratteri" data-testid="reg-password" error={!!errors.password} {...register('password')} />
                <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-[34px] text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <FieldError>{errors.password?.message}</FieldError>
              </div>
              <div>
                <Label required>Conferma password</Label>
                <Input type={showPassword ? 'text' : 'password'} placeholder="ripeti password" data-testid="reg-password-confirm" error={!!errors.passwordConfirm} {...register('passwordConfirm')} />
                <FieldError>{errors.passwordConfirm?.message}</FieldError>
              </div>
            </div>
          </section>

          {/* === Anagrafica azienda === */}
          <section className="rounded-2xl border bg-white shadow-sm p-6" data-testid="section-azienda">
            <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[hsl(214,58%,41%)]" /> Dati azienda
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label required>Ragione sociale</Label>
                <Input data-testid="reg-rag-sociale" error={!!errors.ragioneSociale} {...register('ragioneSociale')} />
                <FieldError>{errors.ragioneSociale?.message}</FieldError>
              </div>
              <div>
                <Label required>Partita IVA</Label>
                <Input placeholder="IT01234567897" data-testid="reg-piva" error={!!errors.partitaIva} {...register('partitaIva')} />
                <FieldError>{errors.partitaIva?.message}</FieldError>
              </div>
              <div>
                <Label>Codice Fiscale</Label>
                <Input data-testid="reg-cf" error={!!errors.codiceFiscale} {...register('codiceFiscale')} />
                <FieldError>{errors.codiceFiscale?.message}</FieldError>
              </div>
              <div>
                <Label>PEC</Label>
                <Input type="email" placeholder="azienda@pec.it" data-testid="reg-pec" error={!!errors.pec} {...register('pec')} />
                <FieldError>{fieldOnlyError(errors.pec?.message)}</FieldError>
              </div>
              <div>
                <Label>Codice SDI</Label>
                <Input placeholder="7 caratteri" maxLength={7} data-testid="reg-sdi" error={!!errors.codiceSdi} {...register('codiceSdi')} />
                <FieldError>{fieldOnlyError(errors.codiceSdi?.message)}</FieldError>
              </div>
              {/* Riga di avviso condivisa PEC/SDI: verde quando è soddisfatta,
                  rossa quando la validazione l'ha già segnalata, altrimenti
                  ambra come promemoria non ancora bloccante. */}
              <p
                className={`sm:col-span-2 text-[11px] -mt-1 ${
                  pecOrSdiOk ? 'text-emerald-600' : pecSdiError ? 'text-red-500' : 'text-amber-600'
                }`}
                data-testid="reg-pec-sdi-hint"
              >
                {pecOrSdiOk
                  ? '✓ Compilato'
                  : `⚠️ ${PEC_SDI_MESSAGE} per la fatturazione elettronica`}
              </p>
              <div className="sm:col-span-2">
                <Label>Telefono azienda</Label>
                <Input type="tel" placeholder="+39 ..." data-testid="reg-telefono" error={!!errors.telefonoAzienda} {...register('telefonoAzienda')} />
                <FieldError>{errors.telefonoAzienda?.message}</FieldError>
              </div>
            </div>
          </section>

          {/* === Anagrafica persona === */}
          <section className="rounded-2xl border bg-white shadow-sm p-6" data-testid="section-persona">
            <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-[hsl(214,58%,41%)]" /> Dati referente
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label required>Nome</Label>
                <Input data-testid="reg-nome" error={!!errors.nome} {...register('nome')} />
                <FieldError>{errors.nome?.message}</FieldError>
              </div>
              <div>
                <Label required>Cognome</Label>
                <Input data-testid="reg-cognome" error={!!errors.cognome} {...register('cognome')} />
                <FieldError>{errors.cognome?.message}</FieldError>
              </div>
              <div className="sm:col-span-2">
                <Label required>Cellulare</Label>
                <Input type="tel" placeholder="+39 ..." autoComplete="mobile tel" inputMode="tel" data-testid="reg-cellulare" error={!!errors.cellulare} {...register('cellulare')} />
                <FieldError>{errors.cellulare?.message}</FieldError>
              </div>
            </div>
          </section>

          {/* === Sede legale === */}
          <section className="rounded-2xl border bg-white shadow-sm p-6" data-testid="section-sede-legale">
            <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[hsl(214,58%,41%)]" /> Sede legale
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
              <div className="sm:col-span-4">
                <Label required>Indirizzo</Label>
                <Input data-testid="reg-indirizzo" error={!!errors.indirizzo} {...register('indirizzo')} />
                <FieldError>{errors.indirizzo?.message}</FieldError>
              </div>
              <div className="sm:col-span-2">
                <Label required>CAP</Label>
                <Input maxLength={5} inputMode="numeric" data-testid="reg-cap" error={!!errors.cap} {...register('cap')} />
                <FieldError>{errors.cap?.message}</FieldError>
              </div>
              <div className="sm:col-span-3">
                <Label required>Città</Label>
                <Input data-testid="reg-citta" error={!!errors.citta} {...register('citta')} />
                <FieldError>{errors.citta?.message}</FieldError>
              </div>
              <div className="sm:col-span-3">
                <Label required>Provincia</Label>
                <Controller
                  name="provincia"
                  control={control}
                  render={({ field }) => (
                    <ProvinciaSelect value={field.value} onChange={field.onChange} testid="reg-provincia" />
                  )}
                />
                <FieldError>{errors.provincia?.message}</FieldError>
              </div>
            </div>

            {/* Toggle sede operativa */}
            <div className="mt-4 pt-4 border-t">
              <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="reg-toggle-sede-op">
                <input
                  type="checkbox"
                  className="rounded border-input accent-[hsl(214,58%,41%)]"
                  data-testid="reg-checkbox-sede-op"
                  {...register('hasSedeOperativa')}
                />
                <Plus className="w-3.5 h-3.5 text-[hsl(214,58%,41%)]" />
                Aggiungi sede operativa (se diversa dalla sede legale)
              </label>
            </div>
          </section>

          {/* === Sede operativa (condizionale) === */}
          {hasSedeOperativa && (
            <section className="rounded-2xl border bg-white shadow-sm p-6" data-testid="section-sede-operativa">
              <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-600" /> Sede operativa
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                <div className="sm:col-span-4">
                  <Label required>Indirizzo</Label>
                  <Input data-testid="reg-sedeop-indirizzo" error={!!errors.sedeOpIndirizzo} {...register('sedeOpIndirizzo')} />
                  <FieldError>{errors.sedeOpIndirizzo?.message}</FieldError>
                </div>
                <div className="sm:col-span-2">
                  <Label required>CAP</Label>
                  <Input maxLength={5} inputMode="numeric" data-testid="reg-sedeop-cap" error={!!errors.sedeOpCap} {...register('sedeOpCap')} />
                  <FieldError>{errors.sedeOpCap?.message}</FieldError>
                </div>
                <div className="sm:col-span-3">
                  <Label required>Città</Label>
                  <Input data-testid="reg-sedeop-citta" error={!!errors.sedeOpCitta} {...register('sedeOpCitta')} />
                  <FieldError>{errors.sedeOpCitta?.message}</FieldError>
                </div>
                <div className="sm:col-span-3">
                  <Label required>Provincia</Label>
                  <Controller
                    name="sedeOpProvincia"
                    control={control}
                    render={({ field }) => (
                      <ProvinciaSelect value={field.value ?? ''} onChange={field.onChange} testid="reg-sedeop-provincia" />
                    )}
                  />
                  <FieldError>{errors.sedeOpProvincia?.message}</FieldError>
                </div>
              </div>
            </section>
          )}

          {/* === Profilazione commerciale === */}
          <section className="rounded-2xl border bg-white shadow-sm p-6" data-testid="section-profilazione">
            <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-[hsl(214,58%,41%)]" /> Profilazione commerciale
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">

              {/* ── Marche di interesse ── */}
              <div ref={marcaRef} className="relative">
                <Label required>Marche di interesse</Label>

                {marcheInteresse[0] ? (
                  /* Stato: marca selezionata → chips + bottone + */
                  <div className="flex flex-wrap items-center gap-1.5 min-h-[2.5rem]" data-testid="reg-marche-chips">
                    {/* Chip principale — blu piena */}
                    <button
                      type="button"
                      onClick={() => setMarche(marcheInteresse.slice(1))}
                      data-testid="reg-marca-chip-primary"
                      className="inline-flex items-center gap-1 pl-3 pr-2 py-1 rounded-full text-xs font-semibold bg-[hsl(214,58%,41%)] text-white hover:bg-red-500 transition-colors"
                      title="Clicca per rimuovere"
                    >
                      {marcheInteresse[0]}
                      <span className="text-[11px] leading-none opacity-80">×</span>
                    </button>
                    {/* Chips extra — grigie */}
                    {marcheInteresse.slice(1).map(m => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => removeMarca(m)}
                        data-testid={`reg-marca-chip-${m}`}
                        className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium bg-secondary text-foreground border hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-colors"
                      >
                        {m}
                        <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-muted text-[10px] leading-none">×</span>
                      </button>
                    ))}
                    {/* Bottone + */}
                    <button
                      type="button"
                      onClick={() => { setMarcaSearch(''); setShowMarcaModal(true); }}
                      data-testid="reg-aggiungi-marche"
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-dashed border-[hsl(214,58%,41%)] text-[hsl(214,58%,41%)] hover:bg-blue-50 transition-colors flex-shrink-0"
                      title="Aggiungi altra marca"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  /* Stato: nessuna marca → trigger modale */
                  <button
                    type="button"
                    onClick={() => { setMarcaSearch(''); setShowMarcaModal(true); }}
                    data-testid="reg-marca"
                    className={`w-full h-10 px-3 rounded-lg border bg-white text-sm text-muted-foreground text-left flex items-center justify-between transition-colors ${
                      errors.marcheInteresse ? 'border-red-400' : 'hover:border-[hsl(214,58%,41%)]'
                    }`}
                  >
                    <span>Seleziona marca principale...</span>
                    <ChevronDown className="w-4 h-4 opacity-40 flex-shrink-0" />
                  </button>
                )}
                <FieldError>{errors.marcheInteresse?.message}</FieldError>

                {/* Mini modale di ricerca */}
                {showMarcaModal && (
                  <div className="absolute z-50 top-full left-0 mt-1 w-full bg-white border rounded-xl shadow-xl p-3" data-testid="reg-marca-modal">
                    <input
                      autoFocus
                      type="text"
                      placeholder="Cerca marca..."
                      value={marcaSearch}
                      onChange={e => setMarcaSearch(e.target.value)}
                      className="w-full h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)] focus:border-transparent"
                      data-testid="reg-marca-search"
                    />
                    <div className="mt-2 max-h-52 overflow-y-auto">
                      {brands
                        .filter(b => !marcheInteresse.includes(b) && b.toLowerCase().includes(marcaSearch.toLowerCase()))
                        .map(b => (
                          <button
                            key={b}
                            type="button"
                            onClick={() => {
                              setMarche([...marcheInteresse, b]);
                              setShowMarcaModal(false);
                              setMarcaSearch('');
                            }}
                            className="w-full text-left px-3 py-1.5 text-sm rounded-lg hover:bg-blue-50 hover:text-[hsl(214,58%,41%)] transition-colors"
                          >
                            {b}
                          </button>
                        ))
                      }
                    </div>
                  </div>
                )}
              </div>

              {/* ── Parco macchine ── */}
              <div>
                <Label>Parco macchine stimato</Label>
                <Controller
                  name="parcoMacchineStimato"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      value={field.value}
                      onChange={field.onChange}
                      testid="reg-parco"
                      placeholder="Seleziona (opzionale)"
                      searchPlaceholder="Cerca..."
                      options={PARCO_OPTIONS}
                    />
                  )}
                />
              </div>
            </div>
          </section>

          {/* === Privacy === */}
          <section className="rounded-2xl border bg-white shadow-sm p-6">
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-input accent-[hsl(214,58%,41%)]"
                data-testid="reg-checkbox-privacy"
                {...register('accettaPrivacy')}
              />
              <span>
                Accetto la <a href={IUBENDA_PRIVACY_URL} className="text-[hsl(214,58%,41%)] hover:underline" target="_blank" rel="noopener noreferrer">privacy policy</a> e i <Link to="/legale/condizioni" className="text-[hsl(214,58%,41%)] hover:underline" target="_blank">termini di servizio</Link>. <span className="text-red-500">*</span>
              </span>
            </label>
            <FieldError>{errors.accettaPrivacy?.message}</FieldError>
          </section>

          {/* === Submit === */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
            <p className="text-xs text-muted-foreground">
              I campi contrassegnati con <span className="text-red-500">*</span> sono obbligatori.
            </p>
            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="reg-submit"
              className="w-full sm:w-auto h-11 px-6 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-semibold hover:bg-[hsl(214,58%,46%)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" />
              {isSubmitting ? 'Invio in corso…' : 'Invia richiesta'}
            </button>
          </div>
        </form>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Hai già un account?{' '}
          <Link to="/accedi" className="text-[hsl(214,58%,41%)] hover:underline font-medium">Accedi</Link>
        </p>
      </div>
    </div>
  );
}
